import { Temporal } from "temporal-polyfill";

export interface PortfolioHistorySeries {
  timestamp?: readonly (number | null | undefined)[];
  equity?: readonly (number | null | undefined)[];
}

export interface PortfolioHistoryChartPoint {
  time: number;
  value: number;
}

export const interpolatePortfolioHistoryValue = (
  data: readonly PortfolioHistoryChartPoint[],
  time: number,
): number | undefined => {
  const first = data[0];
  const last = data.at(-1);
  if (!first || !last || time < first.time || time > last.time) {
    return undefined;
  }

  let low = 0;
  let high = data.length - 1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const point = data[middle];
    if (point.time === time) {
      return point.value;
    }
    if (point.time < time) {
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  const left = data[high];
  const right = data[low];
  if (!left || !right || right.time === left.time) {
    return undefined;
  }

  const progress = (time - left.time) / (right.time - left.time);
  return left.value + (right.value - left.value) * progress;
};

export interface PortfolioChange {
  change?: number;
  changePercent?: number;
}

export const alignPortfolioHistoryToEquity = (
  data: readonly PortfolioHistoryChartPoint[],
  currentEquity: number | undefined,
): PortfolioHistoryChartPoint[] => {
  const latestHistoryValue = data.at(-1)?.value;
  if (
    currentEquity === undefined ||
    currentEquity <= 0 ||
    latestHistoryValue === undefined
  ) {
    return [...data];
  }

  const offset = currentEquity - latestHistoryValue;
  return data.map(({ time, value }) => ({ time, value: value + offset }));
};

export const getPortfolioChange = (
  data: readonly PortfolioHistoryChartPoint[],
): PortfolioChange => {
  const start = data[0]?.value;
  const end = data.at(-1)?.value;
  if (start === undefined || end === undefined || start <= 0 || end <= 0) {
    return {};
  }

  const change = end - start;
  return {
    change,
    changePercent: (change / start) * 100,
  };
};

export const clipPortfolioHistoryToMonths = (
  data: readonly PortfolioHistoryChartPoint[],
  months: number | undefined,
): PortfolioHistoryChartPoint[] => {
  const lastPoint = data.at(-1);
  if (!lastPoint || months == null) {
    return [...data];
  }

  const cutoffTime = Math.floor(
    Temporal.Instant.fromEpochMilliseconds(lastPoint.time * 1_000)
      .toZonedDateTimeISO("UTC")
      .subtract({ months }).epochMilliseconds / 1_000,
  );
  return data.filter(({ time }) => time >= cutoffTime);
};

export interface PortfolioComparisonDataSeries {
  data: readonly PortfolioHistoryChartPoint[];
  symbol: string;
}

export type PortfolioComparisonColor =
  | "blue"
  | "green"
  | "orange"
  | "purple"
  | "pink"
  | "teal";

export interface PortfolioComparisonChartSeries
  extends PortfolioComparisonDataSeries {
  color: PortfolioComparisonColor;
  hasError: boolean;
  isLoading: boolean;
}

export const toPercentageChangeData = (
  data: readonly PortfolioHistoryChartPoint[],
): PortfolioHistoryChartPoint[] => {
  const baselineIndex = data.findIndex(({ value }) => value !== 0);
  if (baselineIndex === -1) {
    return [];
  }

  const baseline = data[baselineIndex].value;
  if (baseline < 0) {
    return [];
  }

  return data.slice(baselineIndex).map(({ time, value }) => ({
    time,
    value: ((value - baseline) / baseline) * 100,
  }));
};

export const toComparablePercentageSeries = (
  portfolioData: readonly PortfolioHistoryChartPoint[],
  comparisonSeries: readonly PortfolioComparisonDataSeries[],
) => {
  const portfolioStart = portfolioData[0]?.time;
  const portfolioEnd = portfolioData.at(-1)?.time;
  if (portfolioStart == null || portfolioEnd == null) {
    return {
      portfolioData: [],
      comparisonSeries: comparisonSeries.map(({ symbol }) => ({
        data: [],
        symbol,
      })),
    };
  }

  const truncatedSeries = comparisonSeries.map(({ data, symbol }) => ({
    data: data.filter(
      ({ time }) => time >= portfolioStart && time <= portfolioEnd,
    ),
    symbol,
  }));

  return {
    portfolioData: toPercentageChangeData(portfolioData),
    comparisonSeries: truncatedSeries.map(({ data, symbol }) => ({
      data: toPercentageChangeData(data),
      symbol,
    })),
  };
};

export const toPortfolioComparisonChartData = (
  portfolioData: readonly PortfolioHistoryChartPoint[],
  comparisonSeries: readonly PortfolioComparisonChartSeries[],
) => {
  if (comparisonSeries.length === 0) {
    return { portfolioPercentageData: [], comparisonSeries: [] };
  }

  const normalized = toComparablePercentageSeries(
    portfolioData,
    comparisonSeries,
  );
  return {
    portfolioPercentageData: normalized.portfolioData,
    comparisonSeries: comparisonSeries.map((series, index) => ({
      ...series,
      data: normalized.comparisonSeries[index]?.data ?? [],
    })),
  };
};

export const toPortfolioHistoryChartData = ({
  timestamp = [],
  equity = [],
}: PortfolioHistorySeries): PortfolioHistoryChartPoint[] => {
  const pointsByTime = new Map<number, number>();
  const length = Math.min(timestamp.length, equity.length);

  for (let index = 0; index < length; index++) {
    const rawTimestamp = timestamp[index];
    const rawEquity = equity[index];

    if (rawTimestamp == null || rawEquity == null) {
      continue;
    }

    const timestampValue = Number(rawTimestamp);
    const value = Number(rawEquity);
    if (!Number.isFinite(timestampValue) || !Number.isFinite(value)) {
      continue;
    }

    const milliseconds =
      timestampValue < 1_000_000_000_000
        ? timestampValue * 1_000
        : timestampValue;
    const time = Math.floor(milliseconds / 1_000);

    if (!Number.isSafeInteger(time)) {
      continue;
    }

    pointsByTime.set(time, value);
  }

  const points = Array.from(pointsByTime, ([time, value]) => ({
    time,
    value,
  })).sort((left, right) => left.time - right.time);

  const firstNonZeroIndex = points.findIndex(({ value }) => value !== 0);
  return firstNonZeroIndex === -1 ? points : points.slice(firstNonZeroIndex);
};
